<template>
  <div v-if="visible" class="gamanza-sidebar-wrapper" :style="collapsedClipStyle">
    <div :id="widgetId" class="gamification_widget" data-type="sidebar"></div>
  </div>
</template>

<script>
import gamanzaWidgetService from '@/services/gamanzaWidgetService';

export default {
  name: 'GamanzaSidebar',
  props: ['skin', 'open'],
  data() {
    return {
      widgetId: `gamanza-sidebar-widget-${this._uid}`,
      gameClickHandler: null,
      resizeObserver: null,
      sidebarCollapsed: false,
      sidebarStateObserver: null,
      sidebarMutationObserver: null,
      sidebarStateElement: null,
      compactHeaderObserver: null,
      compactHeaderElement: null,
      compactHeaderWidth: 0,
      sidebarAnimating: false,
      sidebarAnimationTimeout: null,
      sidebarPendingCollapsed: null
    }
  },
  computed: {
    gamanzaReady() {
      return this.$store.getters['getGamanzaReady'];
    },
    visible() {
      return Boolean(
        this.open &&
        this.gamanzaReady &&
        this.skin?.gamanza?.enabled &&
        this.skin?.gamanza?.mainFrame?.sidebar
      );
    },
    collapsedClipStyle() {
      if (!this.sidebarCollapsed || !this.compactHeaderWidth || this.sidebarAnimating) return {};

      return {
        clipPath: `inset(0 calc(100% - ${this.compactHeaderWidth}px) 0 0)`
      };
    }
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.sidebarCollapsed = window.matchMedia('(max-width: 768px)').matches;
        this.observeSidebar();
        this.reloadWidget();
      } else {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.disconnectSidebarStateObserver();
        this.clearSidebarAnimation();
        this.sidebarCollapsed = false;
        this.compactHeaderWidth = 0;
        this.$emit('sidebar-expanded', false);
      }
    }
  },
  mounted() {
    this.gameClickHandler = (event) => this.onGameClick(event);
    document.addEventListener('click', this.gameClickHandler, true);
  },
  beforeDestroy() {
    this.resizeObserver?.disconnect();
    this.disconnectSidebarStateObserver();
    this.clearSidebarAnimation();

    if (this.gameClickHandler) {
      document.removeEventListener('click', this.gameClickHandler, true);
    }
  },
  methods: {
    onGameClick(event) {
      if (!this.visible) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const sidebarToggle = target.closest(
        '[class*="sidebarMinimalistButton"], [class*="sidebarToggleArrow"], [class*="sidebarHeaderMinimalistButton"]'
      );
      if (sidebarToggle && window.matchMedia('(max-width: 768px)').matches) {
        this.startSidebarAnimation();
      }

      const gameLink = target.closest('a.GamanzaTypography-root');
      if (!gameLink || !gameLink.closest('.GamanzaGrid-root')) return;

      const gameName = gameLink.querySelector(':scope > p')?.textContent?.trim()
        || gameLink.querySelector(':scope > img')?.alt?.trim();
      if (!gameName) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.$root.$emit('gamanza-game-selected', gameName);
      setTimeout(() => {
        this.closeGamanzaDialog();
        this.collapseSidebar();
      }, 0);
    },
    closeGamanzaDialog() {
      const dialog = Array.from(document.querySelectorAll('.GamanzaDialog-root, .GamanzaModal-root'))
        .find((element) => element.querySelector('.GamanzaGrid-root'));
      const closeButton = dialog?.querySelector('[aria-label="Close"], button[class*="close"], button[class*="Close"]');

      closeButton?.click();
    },
    collapseSidebar() {
      const widget = document.getElementById(this.widgetId);
      const sidebar = widget?.parentElement;

      if (!sidebar || sidebar.getBoundingClientRect().width < 300) return;

      widget.querySelector('[class*="sidebarToggleArrow"], [class*="sidebarHeaderMinimalistButton"]')?.click();
    },
    clearSidebarAnimation() {
      if (this.sidebarAnimationTimeout) {
        clearTimeout(this.sidebarAnimationTimeout);
      }

      this.sidebarAnimating = false;
      this.sidebarAnimationTimeout = null;
      this.sidebarPendingCollapsed = null;
    },
    startSidebarAnimation() {
      this.clearSidebarAnimation();
      this.sidebarAnimating = true;
      this.sidebarPendingCollapsed = !this.sidebarCollapsed;
      this.sidebarAnimationTimeout = setTimeout(() => this.clearSidebarAnimation(), 1500);
    },
    disconnectSidebarStateObserver() {
      this.sidebarStateObserver?.disconnect();
      this.sidebarMutationObserver?.disconnect();
      this.compactHeaderObserver?.disconnect();
      this.sidebarStateObserver = null;
      this.sidebarMutationObserver = null;
      this.sidebarStateElement = null;
      this.compactHeaderObserver = null;
      this.compactHeaderElement = null;
    },
    measureCompactHeader() {
      if (!window.matchMedia('(max-width: 768px)').matches || !this.sidebarCollapsed) {
        this.compactHeaderWidth = 0;
        return;
      }

      const widget = document.getElementById(this.widgetId);
      const wrapper = widget?.parentElement;
      const compactCloseButton = widget?.querySelector('button[class*="sidebarMinimalistButton"]');
      if (!wrapper || !compactCloseButton) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const compactCloseButtonRect = compactCloseButton.getBoundingClientRect();
      const rightEdge = Math.min(wrapperRect.right, compactCloseButtonRect.right);
      const width = Math.ceil(rightEdge - wrapperRect.left);

      if (width > 0) {
        this.compactHeaderWidth = width;
      }

      if (compactCloseButton === this.compactHeaderElement) return;

      this.compactHeaderObserver?.disconnect();
      this.compactHeaderElement = compactCloseButton;
      this.compactHeaderObserver = new ResizeObserver(() => this.measureCompactHeader());
      this.compactHeaderObserver.observe(compactCloseButton);
    },
    scheduleCompactHeaderMeasurement() {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => this.measureCompactHeader());
      });
    },
    observeSidebar() {
      if (typeof ResizeObserver === 'undefined') return;

      this.$nextTick(() => {
        this.resizeObserver?.disconnect();

        const widget = document.getElementById(this.widgetId);
        const sidebar = widget?.parentElement;
        if (!sidebar) return;

        this.resizeObserver = new ResizeObserver((entries) => {
          if (window.matchMedia('(max-width: 768px)').matches) {
            this.measureCompactHeader();
            return;
          }

          const width = entries[0]?.contentRect.width || 0;
          this.$emit('sidebar-expanded', width >= 300);
        });
        this.resizeObserver.observe(sidebar);

        const observeState = () => {
          const panel = widget.querySelector('#GamanzaEngageSidebar');
          if (!panel) return;

          if (panel !== this.sidebarStateElement) {
            this.sidebarStateObserver?.disconnect();
            this.sidebarStateElement = panel;
            this.sidebarStateObserver = new ResizeObserver((entries) => {
              if (!window.matchMedia('(max-width: 768px)').matches) return;

              const height = entries[0]?.contentRect.height || 0;
              this.sidebarCollapsed = height === 0;
              if (this.sidebarAnimating && this.sidebarPendingCollapsed === this.sidebarCollapsed) {
                this.clearSidebarAnimation();
              }
              if (this.sidebarCollapsed) {
                this.scheduleCompactHeaderMeasurement();
              } else {
                this.compactHeaderWidth = 0;
              }
              this.$emit('sidebar-expanded', height > 0);
            });
            this.sidebarStateObserver.observe(panel);
          }
          this.scheduleCompactHeaderMeasurement();
        };

        this.disconnectSidebarStateObserver();
        this.sidebarMutationObserver = new MutationObserver(observeState);
        this.sidebarMutationObserver.observe(widget, { childList: true, subtree: true });
        observeState();
      });
    },
    reloadWidget() {
      this.$nextTick(() => {
        setTimeout(() => gamanzaWidgetService.reloadOne(this.widgetId), 300);
      });
    }
  }
}
</script>

<style scoped>
.gamanza-sidebar-wrapper {
  position: fixed;
  top: 50%;
  left: 0;
  z-index: 1200;
  max-width: 100vw;
  max-height: calc(100vh - 20px);
  transform: translateY(-50%);
}

@media (max-width: 768px) {
  .gamanza-sidebar-wrapper {
    top: 50px;
    width: 100%;
    transform: none;
  }
}
</style>
